import React, { Suspense, forwardRef, lazy, useRef } from 'react';

// Lexical + Prism are heavy, so the editor stays code-split — but React.lazy
// alone flashes its Suspense fallback on every wrapper's first mount, even
// when the chunk is already cached. This module fixes both halves:
//   1. preloadLexicalEditor() lets callers warm the chunk early (app idle,
//      card hover) so the first real mount doesn't hit the network.
//   2. Once the module has resolved, the wrapper renders it synchronously,
//      skipping Suspense entirely — no "Loading editor…" flash.
let LoadedEditor = null;
let loadPromise = null;

export function preloadLexicalEditor() {
  if (!loadPromise) {
    loadPromise = import(/* webpackPrefetch: true */ './LexicalRichEditor').then((mod) => {
      LoadedEditor = mod.default;
      return mod;
    });
  }
  return loadPromise;
}

const LazyEditor = lazy(preloadLexicalEditor);

const LexicalRichEditorLazy = forwardRef(function LexicalRichEditorLazy(
  { fallback = null, ...props },
  ref
) {
  // Latch the render path on first mount: switching element types after the
  // module resolves would unmount/remount a live editor (dropping focus and
  // caret mid-typing). Instances that mounted lazily stay lazy — once the
  // chunk resolved, LazyEditor renders synchronously anyway.
  const directRef = useRef(null);
  if (directRef.current === null) directRef.current = !!LoadedEditor;
  if (directRef.current) return <LoadedEditor ref={ref} {...props} />;
  return (
    <Suspense fallback={fallback}>
      <LazyEditor ref={ref} {...props} />
    </Suspense>
  );
});

export default LexicalRichEditorLazy;
